import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { AccountHolder } from "../../Account";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { useLanguage } from "../../Language";
import { useForm, SubmitHandler } from "react-hook-form";
import axios from "axios";
import { SuccessMessageControl } from "@tlc/ls2pac/app/controls/successMessageControl";

interface CreateUsernameFormInput {
  username: string;
  password: string;
  confirmPassword: string;
}

interface SaveUserLoginInformationReturn {
  success: boolean;
  message: string;
  usernameAlreadyInUse: boolean;
  badCredentials: boolean;
}

export const CreateUsernamePage: React.FC = () => {
  const navigate = useNavigate();
  const { translate } = useLanguage();
  const [errorMessage, setErrorMessage] = React.useState<string>();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<CreateUsernameFormInput>({
    mode: "onBlur",
  });

  const onSubmit: SubmitHandler<CreateUsernameFormInput> = async ({
    password,
    username,
  }) => {
    setErrorMessage(null);

    const { data } = await axios.post<SaveUserLoginInformationReturn>(
      "/account/update/ezLogin",
      {
        password,
        username,
      }
    );

    if (data.success) {
      SuccessMessageControl.show(translate("successCreatingEZLoginMessage"));
      await AccountHolder.fetchUser(true); // trigger refetch of updated user
      navigate("/myaccount/summary");
    } else if (data.usernameAlreadyInUse) {
      setErrorMessage(translate("EZUsernameAlreadyInUseMessage"));
    } else {
      setErrorMessage(translate("errorCreatingEZLoginMessage"));
    }
  };

  return (
    <>
      <Toolbar disableGutters={true}>
        <IconButton
          aria-label="back"
          onClick={() => navigate("/myaccount/summary")}
        >
          <ArrowBackIosIcon />
        </IconButton>
        <Typography variant="h5">{translate("createEZLogin")}</Typography>
      </Toolbar>
      <Box>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Stack direction="column" sx={{
            gap: 2
          }}>
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
            <TextField
              name="username"
              label={translate("ezUsername")}
              type="text"
              required
              autoFocus
              {...register("username", {
                required: translate("usernameIsRequired"),
              })}
              error={!!errors.username}
              helperText={errors.username?.message || " "}
            ></TextField>

            <TextField
              name="password"
              required
              {...register("password", {
                required: translate("passwordIsRequired"),
              })}
              error={!!errors.password}
              helperText={errors.password?.message || " "}
              label={translate("ezPassword")}
              type="password"
            ></TextField>

            <TextField
              name="confirmPassword"
              required
              {...register("confirmPassword", {
                required: translate("confirmPasswordIsRequired"),
                validate: (value) =>
                  value === watch("password") ||
                  translate("passwordAndConfirmPasswordMustMatchError"),
              })}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message || " "}
              label={translate("confirmEZPassword")}
              type="password"
            ></TextField>
            <Box>
              <Button type="submit" variant="contained">
                {translate("save")}
              </Button>
              <Button
                type="button"
                onClick={() => navigate("/myaccount/summary")}
              >
                {translate("cancel")}
              </Button>
            </Box>
          </Stack>
        </form>
      </Box>
    </>
  );
};
